// ─────────────────────────────────────────────────────────────────
// Sehat AI — Medicine / Tablet Extractor
// Scans AI reply text for known OTC medicine names and returns
// structured card data to show in the VoiceOverlay tablet panel.
// ─────────────────────────────────────────────────────────────────

const MEDICINES = [
  // ── Fever / Pain ─────────────────────────────────────────────
  { name: 'Paracetamol',  aliases: ['paracetamol'],              use: 'Fever · Pain',       emoji: '🌡️',  color: '#F59E0B' },
  { name: 'Crocin',       aliases: ['crocin'],                   use: 'Fever · Pain',       emoji: '🌡️',  color: '#F59E0B' },
  { name: 'Combiflam',    aliases: ['combiflam'],                use: 'Pain · Fever',       emoji: '💊',  color: '#F59E0B' },
  { name: 'Ibuprofen',    aliases: ['ibuprofen'],                use: 'Pain · Inflammation',emoji: '💊',  color: '#F59E0B' },
  { name: 'Dolo 650',     aliases: ['dolo'],                     use: 'Fever · Body Ache',  emoji: '🌡️',  color: '#F59E0B' },

  // ── Cold / Allergy ───────────────────────────────────────────
  { name: 'Cetirizine',   aliases: ['cetirizine'],               use: 'Cold · Allergy',     emoji: '🤧',  color: '#38BDF8' },
  { name: 'D-Cold',       aliases: ['d-cold', 'd cold'],         use: 'Cold · Flu',         emoji: '🤧',  color: '#38BDF8' },
  { name: 'Sinarest',     aliases: ['sinarest'],                 use: 'Cold · Sinus',       emoji: '🤧',  color: '#38BDF8' },
  { name: 'Allegra',      aliases: ['allegra', 'fexofenadine'],  use: 'Allergy',            emoji: '🤧',  color: '#38BDF8' },
  { name: 'Montair',      aliases: ['montair', 'montelukast'],   use: 'Allergy · Asthma',   emoji: '🫁',  color: '#38BDF8' },

  // ── Cough ────────────────────────────────────────────────────
  { name: 'Benadryl',     aliases: ['benadryl'],                 use: 'Cough · Cold',       emoji: '😮‍💨', color: '#A78BFA' },
  { name: 'Honitus',      aliases: ['honitus'],                  use: 'Cough · Throat',     emoji: '😮‍💨', color: '#A78BFA' },
  { name: 'Alex',         aliases: ['alex syrup', 'alex cough'], use: 'Cough',              emoji: '😮‍💨', color: '#A78BFA' },
  { name: 'Ascoril',      aliases: ['ascoril'],                  use: 'Cough · Congestion', emoji: '😮‍💨', color: '#A78BFA' },

  // ── Acidity / Digestion ──────────────────────────────────────
  { name: 'Pantoprazole', aliases: ['pantoprazole', 'pan 40'],  use: 'Acidity · GERD',     emoji: '🔥',  color: '#F97316' },
  { name: 'Omeprazole',   aliases: ['omeprazole'],               use: 'Acidity',            emoji: '🔥',  color: '#F97316' },
  { name: 'Eno',          aliases: ['eno'],                      use: 'Acidity · Gas',      emoji: '⚡',  color: '#F97316' },
  { name: 'Digene',       aliases: ['digene'],                   use: 'Acidity · Gas',      emoji: '🔥',  color: '#F97316' },
  { name: 'Gelusil',      aliases: ['gelusil'],                  use: 'Acidity · Heartburn',emoji: '🔥',  color: '#F97316' },
  { name: 'Ranitidine',   aliases: ['ranitidine', 'aciloc'],    use: 'Acidity',            emoji: '🔥',  color: '#F97316' },

  // ── Diarrhoea / Stomach ──────────────────────────────────────
  { name: 'ORS',          aliases: ['ors', 'electral'],          use: 'Dehydration · Loose Motion', emoji: '💧', color: '#22D3EE' },
  { name: 'Loperamide',   aliases: ['loperamide', 'lopamide'],  use: 'Diarrhoea',          emoji: '🚽',  color: '#22D3EE' },
  { name: 'Norflox',      aliases: ['norflox'],                  use: 'Stomach Infection',  emoji: '🦠',  color: '#22D3EE' },
  { name: 'Flagyl',       aliases: ['flagyl', 'metronidazole'], use: 'Stomach Infection',  emoji: '🦠',  color: '#22D3EE' },

  // ── Antibiotic ───────────────────────────────────────────────
  { name: 'Azithromycin', aliases: ['azithromycin', 'azee'],    use: 'Bacterial Infection', emoji: '🦠', color: '#4ADE80' },
  { name: 'Amoxicillin',  aliases: ['amoxicillin'],              use: 'Bacterial Infection', emoji: '🦠', color: '#4ADE80' },

  // ── Vitamin / Supplement ─────────────────────────────────────
  { name: 'Vitamin C',    aliases: ['vitamin c', 'limcee'],     use: 'Immunity',           emoji: '🍊',  color: '#FB923C' },
  { name: 'Zinc',         aliases: ['zinc'],                     use: 'Immunity · Recovery',emoji: '💪',  color: '#FB923C' },
  { name: 'B-Complex',    aliases: ['b-complex', 'bcomplex', 'becosules'], use: 'Energy · Nerves', emoji: '⚡', color: '#FB923C' },

  // ── Sleep / Stress ───────────────────────────────────────────
  { name: 'Melatonin',    aliases: ['melatonin'],                use: 'Sleep',              emoji: '🌙',  color: '#818CF8' },
];

/**
 * Scan AI reply text and return matching medicine cards.
 * Returns max 4 results (most specific matches first, deduped).
 *
 * @param {string} text - Claude's response text
 * @returns {Array<{name,use,emoji,color}>}
 */
export function extractMedicines(text) {
  if (!text) return [];
  const lower = text.toLowerCase();
  const seen = new Set();
  const results = [];

  for (const med of MEDICINES) {
    if (seen.has(med.name)) continue;
    const matched = med.aliases.some(alias => lower.includes(alias));
    if (matched) {
      seen.add(med.name);
      results.push({ name: med.name, use: med.use, emoji: med.emoji, color: med.color });
      if (results.length >= 4) break;
    }
  }

  return results;
}
